// SimEvent → sound requests (IMPLEMENTATION §3 M9, GDD §14 / UX §5 SFX columns). Stateful but pure: no Web
// Audio, no DOM; the caller passes a wall clock in ms. Every number comes from sfx.json `events` / recipes,
// juice.json (`stallBonk`, `events.quickMerge.staggerMs`) and the compiled stall table.
//
// Rules (EVENT_SOUNDS is the per-kind table; the coverage test keeps it equal to the SimEvent union):
//   hit (tap/hold) → dig, start jittered 0..timeJitterMs (hold hits are 7 ticks apart: never metronomic);
//   hit (drill) → one quiet "ambient" dig at most every ambientGapMs × [1 − j, 1 + j) — v0.1 data sets
//     ambientGain 0 (drills silent: UX §5 MachineCycle "v0.1: none", the per-machine SFX is N5 / v0.2);
//   spawn → silent (UX §5 "covered by tap": a tap's spawn arrives a tick after its hit, in another batch;
//     drill spawns come with their drill hit; machine / ghost / overflow spawns are not digs);
//   merge → merge pitched to the pentatonic step P[tier−1+combo] (combo +1 per merge within comboWindowMs,
//     max comboMax); tier ≥ delayMinTier uses `merge.delay`; the merges of one batch (Merge All) are spread
//     quickMerge.staggerMs apart, at most burstMax of them; `via: 'machine'` is silent unless events.merge.machine;
//   bmerge → merge one octave down (P[level−1] + buildingSemitones); built → merge T1 + coin;
//   sell → coin, or coin.big when ≥ bigRatio × the mean of the previous `history` sales; bremove → coin;
//   saleUndone → ui.click at undoSemitones; goal → coin at goalSemitones ("coin high");
//   fire → pop.cork then pop.target targetDelayMs later (a practice shot pops the target only when
//     events.fire.practiceTarget); the cork is pitched gunPitchPerTier^(t−1) only when events.fire.gunPitch
//     (v0.2 per GDD §14 / UX §5; v0.1 data: off);
//   empty → ui.empty (≤ 1 per emptyGapMs); place / bmove → ui.thunk; brotate → ui.click;
//   discover → fanfare; unlock / expand → fanfare.short (one fanfare per batch, the full one wins);
//   reject → ui.bonk, except `cooldown` (silent: M4b #7); bonk (VEIN_BLOCKED) and stall → ui.bonk only when
//     player-caused: a player action on THAT building within stallBonk.playerCausedWithinMs of the stall's
//     start (a stall event arrives graceTicks after it began); stalls only for events.stallClasses;
//     every bonk (reject, deposit, stall) shares one limiter: ≤ 1 per stallBonk.minGapMs.
// Player actions seen in events: hit tap/hold, hand insert, deliver, place, bmove, brotate, bmerge, bremove,
// practice fire, empty (on their building) and reject / sell / saleUndone / hand merges (global). Commands
// without an event (pause, setTarget, paint) are reported by the caller through `noteAction(bid)`.
// `uiBonk` (lead, M6 × M9): the input router's refusal bonk goes through the SAME limiter, so a dispatched drop
// whose `reject` event already bonked does not bonk a second time from the router's callback.
import type { JuiceData, SfxData } from '../data/schema.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { EventKind, SimEvent } from '../sim/events.ts';
import type { SoundId } from './recipes.ts';
import type { PlayRequest } from './SfxEngine.ts';

/** What the audio code reads (GameData satisfies it). */
export interface AudioData {
  sfx: SfxData;
  juice: Pick<JuiceData, 'caps' | 'stallBonk' | 'events'>;
  sim: Pick<SimDefs, 'tickHz' | 'stalls'>;
}

/** Every SimEvent kind: the sounds it may request, or 'silent' (explicitly). */
export const EVENT_SOUNDS = {
  spawn: 'silent',
  move: 'silent',
  merge: ['merge.default', 'merge.delay'],
  insert: 'silent',
  reject: ['ui.bonk'],
  sell: ['coin.default', 'coin.big'],
  saleUndone: ['ui.click'],
  hit: ['dig.default'],
  bonk: ['ui.bonk'],
  craft: 'silent',
  chuteStart: 'silent',
  chuteEnd: 'silent',
  fire: ['pop.cork', 'pop.target'],
  empty: ['ui.empty'],
  stall: ['ui.bonk'],
  unstall: 'silent',
  place: ['ui.thunk'],
  deliver: 'silent',
  built: ['merge.default', 'coin.default'],
  bmerge: ['merge.default'],
  bmove: ['ui.thunk'],
  brotate: ['ui.click'],
  bremove: ['coin.default'],
  unlock: ['fanfare.short'],
  goal: ['coin.default'],
  discover: ['fanfare.default'],
  expand: ['fanfare.short'],
  hint: 'silent',
  reveal: 'silent',
  mergeAll: 'silent',
  change: 'silent',
} as const satisfies { readonly [K in EventKind]: readonly SoundId[] | 'silent' };

export interface MapperConfig {
  steps: readonly number[];
  comboWindowMs: number;
  comboMax: number;
  buildingSemitones: number;
  delayMinTier: number;
  burstGapMs: number;
  burstMax: number;
  machineMerges: boolean;
  targetDelayMs: number;
  gunPitchPerTier: number;
  gunPitch: boolean;
  practiceTarget: boolean;
  dig: {
    timeJitterMs: number;
    ambientGapMs: number;
    ambientGapJitter: number;
    ambientGain: number;
  };
  sell: { bigRatio: number; history: number };
  undoSemitones: number;
  goalSemitones: number;
  emptyGapMs: number;
  bonkWithinMs: number;
  bonkGapMs: number;
  /** Stall id → grace in ms, for the stall classes that may bonk (others are absent). */
  stallGraceMs: ReadonlyMap<string, number>;
}

export function mapperConfig(data: AudioData): MapperConfig {
  const { sfx, juice, sim } = data;
  const r = sfx.recipes;
  const ev = sfx.events;
  const classes: readonly string[] = ev.stallClasses;
  const stallGraceMs = new Map<string, number>();
  for (const s of sim.stalls) {
    if (classes.includes(s.cls)) stallGraceMs.set(s.id, (s.graceTicks * 1000) / sim.tickHz);
  }
  return {
    steps: r.merge.pitch.steps,
    comboWindowMs: r.merge.pitch.comboWindowMs,
    comboMax: r.merge.pitch.comboMax,
    buildingSemitones: r.merge.pitch.buildingSemitones,
    delayMinTier: r.merge.pitch.delayMinTier,
    burstGapMs: juice.events.quickMerge.staggerMs,
    burstMax: ev.merge.burstMax,
    machineMerges: ev.merge.machine,
    targetDelayMs: r.pop.targetDelayMs,
    gunPitchPerTier: r.pop.gunPitchPerTier,
    gunPitch: ev.fire.gunPitch,
    practiceTarget: ev.fire.practiceTarget,
    dig: ev.dig,
    sell: ev.sell,
    undoSemitones: ev.undoSemitones,
    goalSemitones: ev.goalSemitones,
    emptyGapMs: ev.emptyGapMs,
    bonkWithinMs: juice.stallBonk.playerCausedWithinMs,
    bonkGapMs: juice.stallBonk.minGapMs,
    stallGraceMs,
  };
}

export interface EventMapper {
  /** Sound requests for one drained batch (all events of the batch happen at `nowMs`). */
  map(events: readonly SimEvent[], nowMs: number): PlayRequest[];
  /** A player command on building `bid` (null: not tied to a building) that emitted no event. */
  noteAction(bid: number | null, nowMs: number): void;
  /**
   * A refusal the input shows (M6 router: snap-back / reason bubble): a player action and one ui.bonk through
   * the shared bonk limiter (≤ 1 per stallBonk.minGapMs, shared with reject / deposit / stall bonks).
   */
  uiBonk(nowMs: number): PlayRequest[];
  /** Forget combos, limiters and actions (a load / reset). */
  reset(): void;
}

const semi = (s: number): number => 2 ** (s / 12);

function unmapped(_e: never): void {}

export function createEventMapper(cfg: MapperConfig, rand: () => number): EventMapper {
  let lastMergeMs = Number.NEGATIVE_INFINITY;
  let combo = 0;
  let lastBonkMs = Number.NEGATIVE_INFINITY;
  let lastEmptyMs = Number.NEGATIVE_INFINITY;
  let nextAmbientDigMs = Number.NEGATIVE_INFINITY;
  let lastActionMs = Number.NEGATIVE_INFINITY;
  let actions = new Map<number, number>();
  let sales: number[] = [];

  const noteAction = (bid: number | null, nowMs: number): void => {
    lastActionMs = nowMs;
    if (bid === null) return;
    actions.set(bid, nowMs);
    if (actions.size > 256) {
      const keep = cfg.bonkWithinMs + Math.max(0, ...cfg.stallGraceMs.values());
      for (const [k, t] of actions) if (nowMs - t > keep) actions.delete(k);
    }
  };

  const recentOn = (bid: number, nowMs: number, windowMs: number): boolean => {
    const t = actions.get(bid);
    return t !== undefined && nowMs - t <= windowMs;
  };

  const step = (i: number): number => {
    const last = cfg.steps.length - 1;
    return cfg.steps[Math.min(last, Math.max(0, i))] ?? 0;
  };

  return {
    noteAction,
    uiBonk(nowMs) {
      noteAction(null, nowMs);
      if (nowMs - lastBonkMs < cfg.bonkGapMs) return [];
      lastBonkMs = nowMs;
      return [{ sound: 'ui.bonk' }];
    },
    reset() {
      lastMergeMs = Number.NEGATIVE_INFINITY;
      combo = 0;
      lastBonkMs = Number.NEGATIVE_INFINITY;
      lastEmptyMs = Number.NEGATIVE_INFINITY;
      nextAmbientDigMs = Number.NEGATIVE_INFINITY;
      lastActionMs = Number.NEGATIVE_INFINITY;
      actions = new Map();
      sales = [];
    },
    map(events, nowMs) {
      const out: PlayRequest[] = [];
      let burst = 0;
      const batch: { fanfare: PlayRequest | null } = { fanfare: null };

      const playerDig = (): void => {
        out.push({ sound: 'dig.default', delayMs: rand() * cfg.dig.timeJitterMs });
      };
      const ambientDig = (): void => {
        if (cfg.dig.ambientGain <= 0 || nowMs < nextAmbientDigMs) return;
        const j = cfg.dig.ambientGapJitter;
        nextAmbientDigMs = nowMs + cfg.dig.ambientGapMs * (1 - j + 2 * j * rand());
        out.push({
          sound: 'dig.default',
          delayMs: rand() * cfg.dig.timeJitterMs,
          gain: cfg.dig.ambientGain,
        });
      };
      const bonk = (playerCaused: boolean): void => {
        if (!playerCaused || nowMs - lastBonkMs < cfg.bonkGapMs) return;
        lastBonkMs = nowMs;
        out.push({ sound: 'ui.bonk' });
      };
      const fanfareReq = (full: boolean): void => {
        if (batch.fanfare?.sound === 'fanfare.default') return;
        if (batch.fanfare && !full) return;
        batch.fanfare = { sound: full ? 'fanfare.default' : 'fanfare.short' };
      };

      for (const e of events) {
        switch (e.e) {
          case 'hit':
            if (e.src === 'drill') ambientDig();
            else {
              noteAction(e.bid, nowMs);
              playerDig();
            }
            break;
          case 'merge': {
            if (e.via === 'machine' && !cfg.machineMerges) break;
            if (e.via !== 'machine') noteAction(null, nowMs);
            combo =
              nowMs - lastMergeMs <= cfg.comboWindowMs ? Math.min(combo + 1, cfg.comboMax) : 0;
            lastMergeMs = nowMs;
            const k = burst++;
            if (k >= cfg.burstMax) break;
            out.push({
              sound: e.tier >= cfg.delayMinTier ? 'merge.delay' : 'merge.default',
              rate: semi(step(e.tier - 1 + combo)),
              delayMs: k * cfg.burstGapMs,
            });
            break;
          }
          case 'bmerge':
            noteAction(e.bid, nowMs);
            if (e.other !== 0) noteAction(e.other, nowMs);
            out.push({
              sound: 'merge.default',
              rate: semi(step(e.level - 1) + cfg.buildingSemitones),
            });
            break;
          case 'built':
            out.push({ sound: 'merge.default', rate: semi(step(0)) });
            out.push({ sound: 'coin.default' });
            break;
          case 'sell': {
            noteAction(null, nowMs);
            const mean = sales.length > 0 ? sales.reduce((a, b) => a + b, 0) / sales.length : 0;
            const big = sales.length > 0 && e.value >= cfg.sell.bigRatio * mean;
            sales.push(e.value);
            if (sales.length > cfg.sell.history) sales = sales.slice(-cfg.sell.history);
            out.push({ sound: big ? 'coin.big' : 'coin.default' });
            break;
          }
          case 'bremove':
            noteAction(e.bid, nowMs);
            out.push({ sound: 'coin.default' });
            break;
          case 'saleUndone':
            noteAction(null, nowMs);
            out.push({ sound: 'ui.click', rate: semi(cfg.undoSemitones) });
            break;
          case 'goal':
            out.push({ sound: 'coin.default', rate: semi(cfg.goalSemitones) });
            break;
          case 'fire':
            if (e.practice) noteAction(e.bid, nowMs);
            out.push({
              sound: 'pop.cork',
              rate: cfg.gunPitch ? cfg.gunPitchPerTier ** Math.max(0, e.gunTier - 1) : 1,
            });
            if (!e.practice || cfg.practiceTarget) {
              out.push({ sound: 'pop.target', delayMs: cfg.targetDelayMs });
            }
            break;
          case 'empty':
            noteAction(e.bid, nowMs);
            if (nowMs - lastEmptyMs >= cfg.emptyGapMs) {
              lastEmptyMs = nowMs;
              out.push({ sound: 'ui.empty' });
            }
            break;
          case 'reject':
            if (e.reason === 'cooldown') break; // tap-cooldown refusals are silent (M4b #7)
            noteAction(null, nowMs); // a refused drop IS the player action
            bonk(nowMs - lastActionMs <= cfg.bonkWithinMs);
            break;
          case 'bonk':
            bonk(recentOn(e.bid, nowMs, cfg.bonkWithinMs));
            break;
          case 'stall': {
            const grace = cfg.stallGraceMs.get(e.stall);
            if (grace === undefined) break;
            bonk(recentOn(e.bid, nowMs, cfg.bonkWithinMs + grace));
            break;
          }
          case 'place':
            noteAction(e.bid, nowMs);
            out.push({ sound: 'ui.thunk' });
            break;
          case 'bmove':
            noteAction(e.bid, nowMs);
            out.push({ sound: 'ui.thunk' });
            break;
          case 'brotate':
            noteAction(e.bid, nowMs);
            out.push({ sound: 'ui.click' });
            break;
          case 'insert':
            if (e.hand === 1) noteAction(e.bid, nowMs);
            break;
          case 'deliver':
            noteAction(e.bid, nowMs);
            break;
          case 'discover':
            fanfareReq(true);
            break;
          case 'unlock':
          case 'expand':
            fanfareReq(false);
            break;
          case 'spawn':
          case 'move':
          case 'craft':
          case 'chuteStart':
          case 'chuteEnd':
          case 'unstall':
          case 'hint':
          case 'reveal':
          case 'mergeAll':
          case 'change':
            break;
          default:
            unmapped(e); // compile-time exhaustiveness; an unknown kind at runtime stays silent
        }
      }
      if (batch.fanfare) out.push(batch.fanfare);
      return out;
    },
  };
}
